import { useDivisionOptions } from '@inquiries/-hooks/useDivisionOptions'
import { InquirySearchParser } from '@inquiries/-lib/inquiry-search-parser'
import { useMemo, useRef, useState } from 'react'
import { DEFAULT_WORK_LOCATIONS, INQUIRY_ASSIGN_STATUS_NAMES, type InquiryRecruitmentType } from 'shared'
import { useSaveSearchQuery } from '@/hooks/saved-search/useSaveSearchQuery'
import { inquirySavedSearch } from '@/lib/saved-search'

// 検索状態の型
export type InquirySearchFormState = {
  query: string
  exclude: string
  primaryDivisionId: number | null
  isMyAssignment: boolean
  isMyDivision: boolean
  isNoDeal: boolean
  salesMemberName: string
  serviceManagerName: string
  inquiryStatusNames: string[]
  workLocation: { onsite: boolean; offsite: boolean; remote: boolean }
  recruitmentType: InquiryRecruitmentType | ''
  startMonth: string
  isStartAsap: boolean
  estimatedAmountMin: string
  estimatedAmountMax: string
  contractType: string
  assignStatus: string
  assignStartMonth: string
}

// 空の検索状態
const emptyFormState: InquirySearchFormState = {
  query: '',
  exclude: '',
  primaryDivisionId: null,
  isMyAssignment: false,
  isMyDivision: false,
  isNoDeal: false,
  salesMemberName: '',
  serviceManagerName: '',
  inquiryStatusNames: [],
  workLocation: { ...DEFAULT_WORK_LOCATIONS },
  recruitmentType: '',
  startMonth: '',
  isStartAsap: false,
  estimatedAmountMin: '',
  estimatedAmountMax: '',
  contractType: '',
  assignStatus: '',
  assignStartMonth: '',
}

type UseInquirySearchDialogParams = {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSearch: (searchText: string) => void
  currentSearchText?: string
}

export function useInquirySearchDialog({
  open,
  onOpenChange,
  onSearch,
  currentSearchText,
}: UseInquirySearchDialogParams) {
  const [step, setStep] = useState<1 | 2>(1)
  const [queryName, setQueryName] = useState('')
  const [formState, setFormState] = useState<InquirySearchFormState>(emptyFormState)

  const saveSearchQuery = useSaveSearchQuery(inquirySavedSearch)

  // 前回のopen状態を保持
  const prevOpenRef = useRef(open)

  // 検索状態の初期値を計算
  const getInitialFormState = (): InquirySearchFormState => {
    if (currentSearchText) {
      const parser = new InquirySearchParser()
      parser.parse(currentSearchText)
      return {
        query: parser.query,
        exclude: parser.exclude,
        primaryDivisionId: parser.primaryDivisionId,
        isMyAssignment: parser.isMyAssignment,
        isMyDivision: parser.isMyDivision,
        isNoDeal: parser.isNoDeal,
        salesMemberName: parser.salesMemberName,
        serviceManagerName: parser.serviceManagerName,
        inquiryStatusNames: parser.inquiryStatusNames,
        workLocation: parser.workLocation,
        recruitmentType: parser.recruitmentType,
        startMonth: parser.startMonth,
        isStartAsap: parser.isStartAsap,
        estimatedAmountMin: parser.estimatedAmountMin,
        estimatedAmountMax: parser.estimatedAmountMax,
        contractType: parser.contractType,
        assignStatus: parser.assignStatus,
        assignStartMonth: parser.assignStartMonth,
      }
    }
    return emptyFormState
  }

  // openがfalse→trueに変化したときに検索条件を反映（レンダリング中に実行）
  if (open && !prevOpenRef.current) {
    const initialState = getInitialFormState()
    // 状態が異なる場合のみ更新（無限ループ防止）
    if (JSON.stringify(formState) !== JSON.stringify(initialState)) {
      setFormState(initialState)
      setStep(1)
    }
    // step1 のキャンセルは閉じるときのリセット（handleOpenChange）を経由しないため、
    // 前回 step2 で入力した検索条件名を開いたときに破棄する
    setQueryName('')
  }
  prevOpenRef.current = open

  const { divisionOptions: primaryDivisionOptions } = useDivisionOptions()

  // 保存は条件ありのときのみ有効（検索は条件なし＝全件表示なので常に可能）
  const isSaveEnabled = useMemo(() => {
    const parser = new InquirySearchParser(formState)
    return parser.enabled()
  }, [formState])

  // ダイアログの開閉を制御
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      // ダイアログを閉じるときにステップと条件名をリセット
      setStep(1)
      setQueryName('')
    }
    onOpenChange(newOpen)
  }

  // 検索実行
  const handleSearch = () => {
    const parser = new InquirySearchParser(formState)
    const queryString = parser.stringify()
    onSearch(queryString)
    handleOpenChange(false)
  }

  // 検索条件を保存
  const handleSaveQuery = () => {
    if (!queryName) return

    const parser = new InquirySearchParser(formState)
    const queryString = parser.stringify()

    // 新しいクエリを保存
    saveSearchQuery.mutate({
      name: queryName,
      query: queryString,
      createdAt: Date.now(),
    })

    // 検索実行
    handleSearch()
  }

  // ステータスチェックボックスのトグル
  const handleStatusToggle = (statusName: string, checked: boolean) => {
    setFormState((prev) => ({
      ...prev,
      inquiryStatusNames: checked
        ? [...prev.inquiryStatusNames, statusName]
        : prev.inquiryStatusNames.filter((name) => name !== statusName),
    }))
  }

  // TODO: GET /api/inquiry-statuses API実装後にuseQuery経由の取得に差し替え
  const inquiryStatusNames = INQUIRY_ASSIGN_STATUS_NAMES

  return {
    // 状態
    step,
    queryName,
    formState,
    isSaveEnabled,

    // セレクトオプション
    primaryDivisionOptions,
    inquiryStatusNames,

    // ハンドラ
    setStep,
    setQueryName,
    setFormState,
    handleOpenChange,
    handleSearch,
    handleSaveQuery,
    handleStatusToggle,
  }
}
